import { useMemo } from 'react';

import { createWebCitationSource } from '../../../utils/sources.utils';
import InlineCitation from '../../InlineCitation';
import { INLINE_CITATION_WEB } from '../MarkdownRenderer.constants';
import { useMarkdownRendererContext } from '../MarkdownRenderer.context';

import type { HTMLAttributes, ReactNode } from 'react';

type CustomLinkProps = HTMLAttributes<HTMLAnchorElement> & {
  href?: string;
  children?: ReactNode;
  'data-inline-citation'?: string;
};

const CustomLink = ({
  href,
  children,
  ...rest
}: CustomLinkProps): ReactNode => {
  const inlineCitation = rest['data-inline-citation'];

  if (inlineCitation === INLINE_CITATION_WEB && href) {
    return <WebInlineCitation href={href} />;
  }

  if (inlineCitation) {
    return <InlineCitationFromContext href={href} />;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: 'var(--mantine-primary-color-6)',
        textDecoration: 'underline',
      }}
    >
      {children}
    </a>
  );
};

const InlineCitationFromContext = ({ href }: { href?: string }): ReactNode => {
  const { sources, onRemoveSource, isRemoveSourceDisabled } =
    useMarkdownRendererContext();

  const sourceMatch = useMemo(() => {
    if (!href) return undefined;
    return sources.find((source) => source?.url === href);
  }, [href, sources]);

  if (!sourceMatch) return null;

  return (
    <InlineCitation
      source={sourceMatch}
      onRemove={onRemoveSource ? () => onRemoveSource(sourceMatch) : undefined}
      removeDisabled={isRemoveSourceDisabled}
    />
  );
};

/** A citation of a public web page: no indexer metadata, so it is built from the href. */
const WebInlineCitation = ({ href }: { href: string }): ReactNode => {
  const source = useMemo(() => createWebCitationSource(href), [href]);

  return <InlineCitation source={source} />;
};

export default CustomLink;
